import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { map } from "lodash";

// Redux
import { connect } from "react-redux";
import genericAction from "@Actions/generic";

// Components
import IkTable from "@Components/IkTable";
import Screen from "@Components/Screen";

// Utils
import dataHandler from "@Utils/dataHandler";

// Modals
import ConfirmModal from "@Modals/ConfirmModal";
import TagsCrud from "./Modals/TagsCrud";

// Styles
import styles from "./styles.module.css";

const Tags = ({ dispatch, companyName, userName }) => {
	const [tableValue, setTableValue] = useState([]);
	const [openTagsCrud, setOpenTagsCrud] = useState(false);
	const [openConfirmModal, setOpenConfirmModal] = useState(false);
	const [filteredItems, setFilteredItems] = useState([]);
	const [modalOption, setModalOption] = useState("");
	const [categoryList, setCategoryList] = useState([]);
	const [groupsList, setGroupsList] = useState([]);
	const [groupTypes, setGroupTypes] = useState([]);
	const [filteredCount, setFilteredCount] = useState(null);
	const [filteredPrintData, setFilteredPrintData] = useState([]);
	const [statusFilter, setStatusFilter] = useState("active");
	const statusFilterRef = useRef("active");

	const defaultParams = { page: 1, orderKey: "id", orderDir: "desc", rows: 15, isActive: 1 };
	const [currentParams, setCurrentParams] = useState(defaultParams);

	useEffect(() => {
		(async () => {
			const tagsPromise = dataHandler({ dispatch, url: "tags", params: defaultParams });
			const categoryPromise = dispatch(genericAction({ method: "get", route: "category/all" }));
			const groupsPromise = dispatch(genericAction({ method: "get", route: "groups/all" }));
			const typesGroupPromise = dispatch(genericAction({ method: "get", route: "types/groups" }));

			const [tagsResponse, categoryResponse, groupsResponse, typesGroupResponse] = await Promise.all([
				tagsPromise,
				categoryPromise,
				groupsPromise,
				typesGroupPromise,
			]);

			setCategoryList(categoryResponse);
			setGroupTypes(typesGroupResponse);

			const groupsWithType = map(groupsResponse, (group) => {
				const type = typesGroupResponse.find((t) => {
					return t.id === group.typeId;
				});
				const typeName = type ? type.name : "Sin Tipo";
				return {
					...group,
					name: `${typeName} - ${group.name}`,
				};
			});

			setGroupsList(groupsWithType);

			const formattedTags = map(tagsResponse.items, (tag) => {
				const newGroups = map(tag.groups, (group) => {
					const type = typesGroupResponse.find((t) => {
						return t.id === group.typeId;
					});
					const typeName = type ? type.name : "Sin Tipo";
					return {
						...group,
						name: `${typeName} - ${group.name}`,
					};
				});
				return { ...tag, groups: newGroups };
			});

			setFilteredItems(formattedTags);
			setFilteredCount(tagsResponse.count);
		})();
	}, []);

	// Handlers
	const modalData = (name, item) => {
		setTableValue(item);
		setModalOption(name);
		setOpenTagsCrud(true);
	};

	const handleButton = (name, item) => {
		switch (name) {
			case "Watch":
			case "Edit":
				modalData(name, item);
				break;
			case "Eraser":
				setOpenConfirmModal(true);
				setTableValue(item);
				break;
			case "Add":
				modalData(name, []);
				break;
			default:
				break;
		}
	};

	const handlerData = async (params, printable) => {
		const finalParams = { ...params };

		if (statusFilterRef.current === "active") {
			finalParams.isActive = 1;
		} else if (statusFilterRef.current === "inactive") {
			finalParams.isActive = 0;
		} else {
			delete finalParams.isActive;
		}

		const tagsResponse = await dataHandler({ dispatch, url: "tags", params: finalParams });

		const formattedTags = map(tagsResponse.items, (tag) => {
			const newGroups = map(tag.groups, (group) => {
				const type = groupTypes.find((t) => {
					return t.id === group.typeId;
				});
				const typeName = type ? type.name : "Sin Tipo";
				return {
					...group,
					name: `${typeName} - ${group.name}`,
				};
			});
			return { ...tag, groups: newGroups };
		});

		if (printable) {
			setFilteredPrintData(formattedTags);
		} else {
			setFilteredItems(formattedTags);
			setFilteredCount(tagsResponse.count);
			setCurrentParams(params);
		}
	};

	const handleStatusChange = (e, { value }) => {
		setStatusFilter(value);
		statusFilterRef.current = value;
		const newParams = { ...currentParams, page: 1 };
		if (value === "all") {
			delete newParams.isActive;
		} else {
			newParams.isActive = value === "active" ? 1 : 0;
		}
		handlerData(newParams);
	};

	const apiData = async (name) => {
		if (name === "Edit") setOpenTagsCrud(false);
		if (name === "Eraser") {
			setOpenConfirmModal(false);
			await dispatch(genericAction({ method: "delete", route: `tags/${tableValue.id}` }));
		}

		await handlerData(currentParams);
	};

	const setRowStyle = (item) => {
		if (!item.isActive) {
			return { backgroundColor: "rgba(255, 0, 0, 0.24)" };
		}
		return null;
	};

	return (
		<Screen hasHeader hasSidebar headerTitle="Etiquetas">
			<div className={styles.container}>
				<div className={styles.containerTableGroup}>
					<div className={styles.tableGroupList}>
						<IkTable
							data={filteredItems}
							columns={[
								{
									label: "Acciones",
									action: [
										{ content: "trash", color: "#b43333", onClick: "Eraser", type: "icon" },
										{ content: "edit", color: "#3f7ea3", onClick: "Edit", type: "icon" },
									],
									dataPosition: "center",
									width: 1,
									canFilter: false,
									canSort: false,
								},
								{ label: "ID", key: "id", format: "string", dataPosition: "center", width: 1 },
								{
									label: "Activo",
									key: "isActive",
									format: "boolean",
									dataPosition: "center",
									headerPosition: "center",
									boolLabels: { true: "Activo", false: "Inactivo" },
									width: 0.5,
									canFilter: true,
									canSort: true,
								},
								{ label: "Nombre", key: "name", format: "string", headerPosition: "left", width: 1 },
								{ label: "Categoría", key: "category.name", format: "string", headerPosition: "center", width: 1 },
								{
									label: "Grupos",
									key: "groups,name",
									format: "arrayTruncate",
									headerPosition: "left",
									dataPosition: "left",
									width: 15,
								},
							]}
							totalRows={filteredCount}
							filteredPrintData={filteredPrintData}
							defaultParams={currentParams}
							dataHandler={handlerData}
							handlerTable={handleButton}
							addButton={{ label: "Agregar" }}
							mobileCardView
							hidePrintButtons
							cardLine1={[
								{ key: "id", label: "ID" },
								{ key: "isActive", label: "" },
							]}
							cardLine2={[
								{ key: "name", label: "Nombre" },
								{ key: "category.name", label: "Categoría" },
							]}
							cardLine3={[{ key: "groups,name", label: "Grupos" }]}
							searcher={{ width: "40rem", hideOnMobile: true }}
							print={{ printHeader: { company: companyName, title: "Etiquetas", user: userName } }}
							setRowStyle={setRowStyle}
							topButtons={[
								{
									componentType: "DropDown",
									value: statusFilter,
									options: [
										{ key: "all", value: "all", text: "Todas" },
										{ key: "active", value: "active", text: "Activas" },
										{ key: "inactive", value: "inactive", text: "Inactivas" },
									],
									onChange: handleStatusChange,
								},
							]}
						/>
					</div>
				</div>
			</div>

			{openConfirmModal && (
				<ConfirmModal
					visible
					icon="exclamation circle"
					header="Confirmar"
					buttonLabel="Eliminar"
					content="Está seguro que desea eliminar la Etiqueta"
					focus={tableValue.name}
					onConfirm={() => {
						return apiData("Eraser");
					}}
					onDismiss={() => {
						return setOpenConfirmModal(false);
					}}
				/>
			)}

			{openTagsCrud && (
				<TagsCrud
					tableValue={tableValue}
					categoryList={categoryList}
					groupsList={groupsList}
					tagsList={filteredItems}
					modalCrud={modalOption}
					apiData={apiData}
					onClose={() => {
						return setOpenTagsCrud(false);
					}}
				/>
			)}
		</Screen>
	);
};

function mapStateToProps({ user, company }) {
	return {
		companyName: company.name,
		userName: user.name,
	};
}

Tags.propTypes = {
	dispatch: PropTypes.func,
	companyName: PropTypes.string,
	userName: PropTypes.string,
};

export default connect(mapStateToProps)(Tags);
